import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  RadioButtonUnchecked as CircleIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Timeline as TimelineIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Note as NoteIcon,
  InfoOutlined as InfoIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import pathfinderApi, { PathfinderDataPoint } from '../../services/pathfinderApi';
import geoneutronApi from '../../services/geoneutronApi';
import geofenceApi, { Geofence, GeoJSONGeometry } from '../../services/geofenceApi';
import apiService from '../../services/api';
import { GeographicAccessResponse } from '../../types';
import NotesDialog from './NotesDialog';

export interface DrawCircle {
  id: string;
  shapeType: 'circle' | 'polygon'; // Type of shape
  center: [number, number]; // [lat, lon] - centroid for polygons
  radius: number; // meters - only for circles
  coordinates?: number[][][]; // GeoJSON coordinates for polygons [[[lng, lat], [lng, lat], ...]]
  name: string;
  visible: boolean;
  results?: AreaSearchResult[];
  loading?: boolean;
  dwellInsights?: DwellInsight[];
  coLocationInsights?: CoLocationInsight[];
  aiSummary?: string | null;
  mapboxContext?: any; // Geographic context from Mapbox
  geoneutronData?: any; // Raw GeoNeutron API response data
  exploreDeviceDetails?: Record<string, any>; // Additional device details from Explore API
  geofenceId?: number; // ID if saved as geofence
  isResultCircle?: boolean; // Marks this as a result from intersection/union analysis
  participatedInCommonAnalysis?: boolean; // Marks this circle participated in a common/intersection analysis
}

export interface AreaSearchResult {
  entityId: string;
  entityType: string;
  deviceType: string;
  pointCount: number;
  timeRange: {
    start: Date;
    end: Date;
  };
  confidence: number;
  dataPoints?: PathfinderDataPoint[]; // Store the actual points from area search
}

export interface DwellInsight {
  entityId: string;
  deviceType: string;
  dwellCount: number;
  totalDwellTime: number; // seconds
  longestDwell: {
    duration: number; // seconds
    startTime: Date;
    endTime: Date;
    placeName?: string;
    placeType?: string;
  };
  coordinates: [number, number];
  placeName?: string;  // Location name from geocoding
  placeType?: string;  // Location category (cafe, university, etc.)
}

export interface CoLocationInsight {
  entities: string[]; // List of entity IDs that were co-located
  deviceTypes: string[]; // Device types involved
  overlapStart: Date;
  overlapEnd: Date;
  duration: number; // seconds of overlap
  location: [number, number]; // Approximate center
  confidence: number; // 0-1, based on how close they were
  placeName?: string;  // Location name where co-location occurred
  placeType?: string;  // Location category
}

interface AreaSearchProps {
  selectedStudents: string[];
  selectedCourse?: string;
  dateRange: { start: Date | null; end: Date | null };
  timeRange?: { start: Date; end: Date } | null;
  onDrawModeChange: (enabled: boolean, shapeType?: 'circle' | 'polygon') => void;
  onCircleAdd: (circle: DrawCircle) => void;
  onCircleUpdate: (circle: DrawCircle) => void;
  onCircleDelete: (circleId: string) => void;
  drawCircles: DrawCircle[];
  isDrawingMode: boolean;
  drawingShapeType: 'circle' | 'polygon';
  defaultRadius?: number;
  onRadiusChange?: (radius: number) => void;
  onDateRangeChange?: (dateRange: { start: Date; end: Date }) => void;
  onLoadAreaData?: (dataPoints: PathfinderDataPoint[], geoNeutronDevices?: any[], circleInfo?: { center: [number, number], radius: number }, exploreDeviceDetails?: Record<string, any>) => void;  onLoadPoLData?: (dataPoints: PathfinderDataPoint[], circleInfo?: { center: [number, number], radius: number }, deviceId?: string) => void;
  onLoadAreaDwells?: (dwells: any[]) => void;
  measurementSystem?: 'metric' | 'imperial';
  onMeasurementSystemChange?: (system: 'metric' | 'imperial') => void;
  editingCircleId?: string | null;
  onEditingCircleChange?: (circleId: string | null) => void;
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
}

const AreaSearch: React.FC<AreaSearchProps> = ({
  selectedCourse,
  dateRange,
  timeRange,
  onDrawModeChange,
  onCircleAdd,
  onCircleUpdate,
  onCircleDelete,
  drawCircles,
  isDrawingMode,
  drawingShapeType,
  defaultRadius = 100,
  onRadiusChange,
  onDateRangeChange,
  onLoadAreaData,
  onLoadPoLData,
  onLoadAreaDwells,
  measurementSystem = 'metric',
  onMeasurementSystemChange,
  editingCircleId = null,
  onEditingCircleChange,
  onZoomToLocation,
}) => {
  // Theme hook for consistent colors
  const theme = useTheme();
  
  // Ref for scroll container to preserve scroll position during re-renders
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to format date for input fields (local timezone)
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  interface GeoJSONFeature {
    type: "Feature";
    properties: Record<string, any>;
    geometry: GeoJSONGeometry;
  }

  interface FeatureCollection {
    type: "FeatureCollection";
    features: GeoJSONFeature[];
  }

  function polygonsToFeatureCollection(polygons: GeoJSONGeometry[]): FeatureCollection {
    return {
      type: "FeatureCollection",
      features: polygons.map((polygon, index) => ({
        type: "Feature",
        properties: {
          id: `geofence_${index + 1}`
        },
        geometry: polygon
      }))
    };
  }

  function downloadGeoJSON(
    geojson: FeatureCollection,
    filename = "geofences.geojson"
  ) {
    const blob = new Blob(
      [JSON.stringify(geojson, null, 2)],
      { type: "application/geo+json" }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  }

  const searchMode = 'date-range' as string; // Default to date-range mode
  // Removed: circleSubTabs - tabs removed for simpler UI
  const [savedGeofences, setSavedGeofences] = useState<Geofence[]>([]);
  const [savingGeofence, setSavingGeofence] = useState<string | null>(null); // ID of geofence being saved
  const [geofenceNameDialog, setGeofenceNameDialog] = useState<{ open: boolean; circle: DrawCircle | null; name: string }>({
    open: false,
    circle: null,
    name: ''
  });
  const [showGeofenceManager, setShowGeofenceManager] = useState(false);
  const [selectedGeofenceIds, setSelectedGeofenceIds] = useState<number[]>([]); // For multi-select in manager
  const [savedDevices, setSavedDevices] = useState<any[]>([]); // Saved registration IDs from watchlist
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([]); // For multi-select in device manager
  const [showDeviceManager, setShowDeviceManager] = useState(false);
  const [isLoadingPoL, setIsLoadingPoL] = useState(false);
  const hiddenDevices = new Set(); // Track which devices are hidden on map
  const [analysisMode, setAnalysisMode] = useState<'intersection' | 'union'>('intersection'); // Intersection (common) or Union (all)
  const [isAnalyzing, setIsAnalyzing] = useState(false); // Loading state for Find Common/All analysis
  const studentsOnly = true; // Always filter to students only (not RP/SRP)
  const useGeoNeutron = true; // Always query GeoNeutron

  // Notes state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedNotesEntityId, setSelectedNotesEntityId] = useState<number | undefined>(undefined);
  const [selectedNotesEntityTitle, setSelectedNotesEntityTitle] = useState('');
  const [selectedNotesEntityType, setSelectedNotesEntityType] = useState<'watchlist' | 'geofence'>('watchlist');

  const [renameWatchlistDialog, setRenameWatchlistDialog] = useState<{
    open: boolean;
    deviceId: number | null;
    name: string;
  }>({ open: false, deviceId: null, name: '' });
  const [renameWatchlistSaving, setRenameWatchlistSaving] = useState(false);

  // ID/IP Lookup state
  const [idIpSearchInput, setIdIpSearchInput] = useState('');
  const [idIpSearchLoading, setIdIpSearchLoading] = useState(false);
  const [idIpSearchResults, setIdIpSearchResults] = useState<any | null>(null);
  const [idIpSearchError, setIdIpSearchError] = useState<string | null>(null);

  // Geographic Access Control state
  const [geographicAccess, setGeographicAccess] = useState<GeographicAccessResponse | null>(null);
  // Note: geoAccessLoading removed - geographic access loads quickly in background

  // Discovery Preview Dialog state
  const [discoveryDialog, setDiscoveryDialog] = useState<{
    open: boolean;
    loading: boolean;
    circle: DrawCircle | null;
    discovery: {
      deviceCount: number;
      signalCount: number;
      totalDays: number;
    } | null;
    strategy: {
      name: string;
      reason: string;
      chunkDays: number;
      maxConcurrent: number;
      estimatedTime: string;
    } | null;
    searchParams: any;
    flowType: 'area_search' | 'full_analysis'; // Track which flow initiated the dialog
  }>({
    open: false,
    loading: false,
    circle: null,
    discovery: null,
    strategy: null,
    searchParams: null,
    flowType: 'area_search'
  });

  // Conversion helpers
  const metersToFeet = (m: number) => m * 3.28084;
  const feetToMeters = (ft: number) => ft / 3.28084;

  // Geographic access helper - check if a point is within allowed regions
  const isLocationAllowed = useCallback((lat: number, lon: number): { allowed: boolean; region?: string; message?: string } => {
    if (!geographicAccess?.enabled) {
      return { allowed: true, message: 'Geographic access control is disabled' };
    }
    
    if (geographicAccess.worldwide_access) {
      return { allowed: true, message: 'Worldwide access granted' };
    }
    
    if (!geographicAccess.allowed_regions || geographicAccess.allowed_regions.length === 0) {
      return { allowed: false, message: 'No geographic access configured for your groups' };
    }
    
    // Check if point is within any allowed region
    for (const region of geographicAccess.allowed_regions) {
      if (region.bbox) {
        const [minLat, minLon, maxLat, maxLon] = region.bbox;
        // Handle regions that cross the antimeridian
        if (minLon > maxLon) {
          if (lat >= minLat && lat <= maxLat && (lon >= minLon || lon <= maxLon)) {
            return { allowed: true, region: region.name };
          }
        } else {
          if (lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon) {
            return { allowed: true, region: region.name };
          }
        }
      }
    }
    
    const regionNames = geographicAccess.allowed_regions.map(r => r.name).join(', ');
    return { 
      allowed: false, 
      message: `Location is outside your allowed regions: ${regionNames}`
    };
  }, [geographicAccess]);

  // Check if current circle(s) are within allowed regions
  const getCircleAccessStatus = useCallback((circle: DrawCircle): { allowed: boolean; message: string } => {
    if (!geographicAccess?.enabled) {
      return { allowed: true, message: '' };
    }
    
    const result = isLocationAllowed(circle.center[0], circle.center[1]);
    
    if (!result.allowed) {
      return { 
        allowed: false, 
        message: result.message || 'Location not allowed'
      };
    }
    
    // Check radius limit
    if (geographicAccess.max_query_radius_meters > 0 && circle.radius > geographicAccess.max_query_radius_meters) {
      return { 
        allowed: false, 
        message: `Radius ${circle.radius}m exceeds maximum allowed ${geographicAccess.max_query_radius_meters}m`
      };
    }
    
    return { allowed: true, message: result.region ? `Within ${result.region}` : '' };
  }, [geographicAccess, isLocationAllowed]);

  // Load saved geofences on mount and when refreshed
  useEffect(() => {
    const loadGeofences = async () => {
      try {
        const geofences = await geofenceApi.listGeofences({ active_only: true });
        setSavedGeofences(geofences);
      } catch (error) {
      }
    };
    loadGeofences();
    
    // Listen for refresh events
    const handleRefresh = () => {
      loadGeofences();
    };
    window.addEventListener('refresh-geofences', handleRefresh);
    
    return () => {
      window.removeEventListener('refresh-geofences', handleRefresh);
    };
  }, []);

  // Load geographic access restrictions on mount
  useEffect(() => {
    const loadGeographicAccess = async () => {
      try {
        const access = await apiService.getGeographicAccess();
        setGeographicAccess(access);
        console.log('📍 Geographic access loaded:', access);
      } catch (error: any) {
        console.warn('Could not load geographic access:', error);
        // Default to worldwide access if API fails
        setGeographicAccess({
          enabled: false,
          worldwide_access: true,
          allowed_regions: [],
          max_query_radius_meters: 0,
          max_date_range_days: 0
        });
      }
    };
    loadGeographicAccess();
  }, []);

  // Load saved devices from watchlist on mount and when refreshed
  useEffect(() => {
    const loadSavedDevices = async () => {
      try {
        const devices = await pathfinderApi.getWatchlist();
        setSavedDevices(devices || []);
      } catch (error: any) {
        // Silently fail - watchlist may not be available
        setSavedDevices([]);
      }
    };
    loadSavedDevices();
    
    // Listen for refresh events
    const handleRefresh = () => {
      loadSavedDevices();
    };
    window.addEventListener('refresh-watchlist', handleRefresh);
    
    return () => {
      window.removeEventListener('refresh-watchlist', handleRefresh);
    };
  }, []);

  // Open naming dialog for geofence
  const openGeofenceNameDialog = useCallback((circle: DrawCircle) => {
    setGeofenceNameDialog({
      open: true,
      circle,
      name: circle.name
    });
  }, []);

  // Save a drawn shape as a geofence
  const saveAsGeofence = useCallback(async (circle: DrawCircle, customName: string) => {
    setSavingGeofence(circle.id);
    try {
      let geometry;
      
      if (circle.shapeType === 'polygon' && circle.coordinates) {
        // Already in GeoJSON format
        geometry = {
          type: 'Polygon' as const,
          coordinates: circle.coordinates
        };
      } else {
        // Convert circle to polygon
        // circle.center is [lat, lon] but circleToPolygon expects [lng, lat] (GeoJSON format)
        geometry = geofenceApi.circleToPolygon([circle.center[1], circle.center[0]], circle.radius);
      }
      
      const saved = await geofenceApi.createGeofence({
        name: customName,
        description: circle.shapeType === 'circle' 
          ? `Circle: ${circle.radius}m radius at ${circle.center[0].toFixed(5)}, ${circle.center[1].toFixed(5)}`
          : `Polygon with ${circle.coordinates?.[0]?.length || 0} vertices`,
        geometry
      });
      
      
      // Update the circle to include the geofence ID
      onCircleUpdate({ ...circle, geofenceId: saved.id, name: customName });
      
      // Refresh saved geofences list
      const geofences = await geofenceApi.listGeofences({ active_only: true });
      setSavedGeofences(geofences);
      
      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('refresh-geofences'));
      
    } catch (error) {
      alert(`Failed to save geofence: ${error}`);
    } finally {
      setSavingGeofence(null);
    }
  }, [onCircleUpdate]);

  // Handle geofence name dialog submit
  const handleSaveGeofenceWithName = useCallback(async () => {
    if (!geofenceNameDialog.circle || !geofenceNameDialog.name.trim()) return;
    
    await saveAsGeofence(geofenceNameDialog.circle, geofenceNameDialog.name.trim());
    setGeofenceNameDialog({ open: false, circle: null, name: '' });
  }, [geofenceNameDialog, saveAsGeofence]);

  // Delete a saved geofence
  const deleteGeofence = useCallback(async (geofenceId: number) => {
    try {
      await geofenceApi.deleteGeofence(geofenceId);
      
      // Refresh list
      const geofences = await geofenceApi.listGeofences({ active_only: true });
      setSavedGeofences(geofences);
      
      // Remove from drawn circles if loaded
      const circleToRemove = drawCircles.find(c => c.geofenceId === geofenceId);
      if (circleToRemove) {
        onCircleDelete(circleToRemove.id);
      }
    } catch (error) {
      alert(`Failed to delete geofence: ${error}`);
    }
  }, [drawCircles, onCircleDelete]);

  // Load a saved geofence as a drawable area
  const loadGeofence = useCallback((geofence: Geofence) => {
    try {
      const coords = geofence.geometry.coordinates[0];
      
      // Calculate centroid
      let sumLat = 0, sumLng = 0;
      coords.forEach((coord: number[]) => {
        sumLng += coord[0];
        sumLat += coord[1];
      });
      const centroid: [number, number] = [sumLat / coords.length, sumLng / coords.length];
      
      // Check if this was originally saved as a circle by parsing the description
      // Circle descriptions are formatted as: "Circle: XXXm radius at lat, lon"
      const isCircle = geofence.description?.startsWith('Circle:');
      let approximateRadius = 1000; // Default 1km
      
      if (isCircle && geofence.description) {
        // Extract radius from description: "Circle: 500m radius at ..."
        const radiusMatch = geofence.description.match(/Circle:\s*(\d+(?:\.\d+)?)\s*m/);
        if (radiusMatch) {
          approximateRadius = parseFloat(radiusMatch[1]);
        }
      } else {
        // For polygons, calculate an approximate radius based on the bounding box
        let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
        coords.forEach((coord: number[]) => {
          minLng = Math.min(minLng, coord[0]);
          maxLng = Math.max(maxLng, coord[0]);
          minLat = Math.min(minLat, coord[1]);
          maxLat = Math.max(maxLat, coord[1]);
        });
        
        // Calculate diagonal distance in meters (approximate)
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;
        const diagonalDegrees = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
        approximateRadius = Math.max(100, Math.round(diagonalDegrees * 111000 / 2)); // degrees to meters, minimum 100m
      }
      
      const loadedShape: DrawCircle = {
        id: `geofence-${geofence.id}-${Date.now()}`,  // Generate unique ID with timestamp
        shapeType: isCircle ? 'circle' : 'polygon',  // Restore original shape type
        center: centroid,
        radius: approximateRadius,
        coordinates: isCircle ? undefined : geofence.geometry.coordinates, // Only keep coordinates for polygons
        name: geofence.name,
        visible: true,
        results: undefined, // Use undefined so UI shows "Click Analyze Area" instead of "No activity found"
        loading: false,
        geofenceId: geofence.id
      };
      
      onCircleAdd(loadedShape);
      
      // Zoom to the loaded geofence area
      if (onZoomToLocation) {
        // Calculate appropriate zoom level based on radius
        let zoom = 14; // Default
        const radiusKm = approximateRadius / 1000;
        
        if (radiusKm > 50) zoom = 10;
        else if (radiusKm > 20) zoom = 11;
        else if (radiusKm > 10) zoom = 12;
        else if (radiusKm > 5) zoom = 13;
        else if (radiusKm > 2) zoom = 14;
        else if (radiusKm > 0.5) zoom = 14;
        else zoom = 15;
        
        // centroid is [lat, lng] format
        onZoomToLocation(centroid[0], centroid[1], zoom);
      }
    } catch (error) {
      alert(`Failed to load geofence: ${error}`);
    }
  }, [onCircleAdd, onZoomToLocation]);

  // ID/IP Lookup - detect type and search
  const performIdIpSearch = useCallback(async () => {
    if (!idIpSearchInput.trim()) {
      setIdIpSearchError('Please enter at least one ID or IP address');
      return;
    }

    if (!dateRange.start || !dateRange.end) {
      setIdIpSearchError('Please select a date range first');
      return;
    }

    // Check date range (GeoNeutron API has 91-day limit)
    let searchStartDate = dateRange.start;
    let searchEndDate = dateRange.end;
    const daysDiff = (searchEndDate.getTime() - searchStartDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff > 90) {
      // Clamp to 90 days from end date
      searchStartDate = new Date(searchEndDate.getTime() - (90 * 24 * 60 * 60 * 1000));
      alert(`⚠️ Date range exceeds 90-day limit.\n\nThe GeoNeutron API only allows searches up to 90 days.\n\nAutomatically adjusted start date to: ${searchStartDate.toLocaleDateString()}`);
    }

    setIdIpSearchLoading(true);
    setIdIpSearchError(null);
    setIdIpSearchResults(null);

    try {
      // Parse comma-separated values and trim whitespace
      const values = idIpSearchInput
        .split(',')
        .map(v => v.trim())
        .filter(v => v.length > 0);

      if (values.length === 0) {
        setIdIpSearchError('Please enter valid ID or IP values separated by commas');
        return;
      }

      // Detect if values are IP addresses (simple regex check)
      // IP pattern: digits.digits.digits.digits
      const ipPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
      const areIPs = values.every(v => ipPattern.test(v));


      let result;
      if (areIPs) {
        result = await pathfinderApi.searchByIPAddresses({
          ipAddresses: values,
          dateRange: [searchStartDate, searchEndDate]
        });
      } else {
        result = await pathfinderApi.searchByRegistrationIds({
          registrationIds: values,
          dateRange: [searchStartDate, searchEndDate]
        });
      }

      
      setIdIpSearchResults(result);

      // Get location data from 'locations' (normalized)
      const locationData = result.locations || [];
      
      // If we have location data, transform and load it
      if (locationData.length > 0 && onLoadAreaData) {
        
        // GeoNeutron signal fields: latitude, longitude, timestamp (epoch ms), registrationID, etc.
        // IMPORTANT: We need to normalize the data to use snake_case field names for the table
        const normalizedLocationData = locationData.map((loc: any) => {
          // Handle timestamp - can be epoch ms or ISO string
          let timestamp: string;
          if (typeof loc.timestamp === 'number') {
            timestamp = new Date(loc.timestamp).toISOString();
          } else if (loc.eventTime) {
            timestamp = new Date(loc.eventTime).toISOString();
          } else {
            timestamp = new Date().toISOString();
          }
          
          const regId = loc.registrationID || loc.registration_id || loc.ipAddress || 'unknown';
          
          // Return normalized format matching what GeoNeutronDataTable expects
          return {
            registration_id: regId,  // snake_case for table
            latitude: loc.latitude,
            longitude: loc.longitude,
            timestamp: timestamp,
            accuracy: loc.horizontalAccuracyMeters || loc.accuracy || null,
            ip_address: loc.ipAddress || null,
            country: loc.country || null,
            carrier: loc.carrier || null,
            timezone: loc.timezone || null,
            forensic_flags: loc.flags || null,
            // Keep original fields too
            ...loc
          };
        });
        
        const dataPoints = normalizedLocationData.map((loc: any) => {
          const timestamp = new Date(loc.timestamp);
          const regId = loc.registration_id;
          
          return {
            id: `${regId}-${loc.timestamp || Date.now()}`,
            // Internal format: [latitude, longitude]
            coordinates: [loc.latitude, loc.longitude] as [number, number],
            latitude: loc.latitude,
            longitude: loc.longitude,
            timestamp,
            studentId: regId,
            deviceId: regId,
            deviceType: 'external' as const,
            accuracy: loc.accuracy,
            source: 'geoneutron',
            // Include additional GeoNeutron fields
            metadata: {
              altitude: loc.altitude,
              speed: loc.speed,
              bearing: loc.bearing,
              carrier: loc.carrier,
              country: loc.country,
              timezone: loc.timezone,
              ipAddress: loc.ip_address
            }
          };
        });

        onLoadAreaData(dataPoints, normalizedLocationData);
      } else {
      }
    } catch (error: any) {
      setIdIpSearchError(error.message || 'Search failed');
    } finally {
      setIdIpSearchLoading(false);
    }
  }, [idIpSearchInput, dateRange, onLoadAreaData]);

  // Helper to estimate search time based on strategy
  const getEstimatedTime = (strategyName: string): string => {
    if (strategyName === 'single_request') return '5-15 seconds';
    if (strategyName === 'temporal_partitioning') return '15-30 seconds';
    if (strategyName === 'temporal_aggressive') return '30-60 seconds';
    if (strategyName === 'temporal_very_aggressive') return '1-2 minutes';
    if (strategyName === 'temporal_extreme') return '2-4 minutes';
    return '30-90 seconds';
  };

  // Direct search (skips discovery popup) - used after user confirms or as fallback
  const performAreaSearchDirect = useCallback(async (circle: DrawCircle) => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }

    // Update circle to show loading state
    const updatedCircle = { ...circle, loading: true };
    onCircleUpdate(updatedCircle);

    try {
      
      // Determine time range based on search mode
      let searchStartDate: Date;
      let searchEndDate: Date;
      
      if (searchMode === 'current-time' && timeRange) {
        // Use current time slider window with some buffer (e.g., ±30 minutes)
        const bufferMinutes = 30;
        searchStartDate = new Date(timeRange.start.getTime() - (bufferMinutes * 60 * 1000));
        searchEndDate = new Date(timeRange.end.getTime() + (bufferMinutes * 60 * 1000));
      } else {
        // Use full date range
        searchStartDate = dateRange.start;
        searchEndDate = dateRange.end;
      }

      // GeoNeutron area search - Always query external data
      let geoneutronData: any = null;
      let geoneutronResults: AreaSearchResult[] = [];
        try {
          
          // Check date range (GeoNeutron API has 91-day limit)
          const daysDiff = (searchEndDate.getTime() - searchStartDate.getTime()) / (1000 * 60 * 60 * 24);
          if (daysDiff > 90) {
            // Clamp to 90 days from end date
            searchStartDate = new Date(searchEndDate.getTime() - (90 * 24 * 60 * 60 * 1000));
          }
          
          
          // Warn if dates are in the future
          if (searchEndDate > new Date()) {
          }
          
          // Call GeoNeutron DISCOVER-FIRST API for optimal performance in populated areas
          // This first discovers device count, then dynamically partitions the query
          const geoSearchParams: any = {
            dateRange: [searchStartDate, searchEndDate],
            registrationIds: [], // Empty array = search ALL devices in area
            sampleDays: 3 // Sample 3 days to estimate device density
          };
          
          // Use polygon if available, otherwise use circle parameters
          if (circle.shapeType === 'polygon' && circle.coordinates) {
            geoSearchParams.polygons = [{
              id: circle.id,
              coordinates: circle.coordinates
            }];
          } else {
            geoSearchParams.areas = [{
              center: circle.center,
              radius: circle.radius,
              id: circle.id
            }];
          }
          
          geoneutronData = await pathfinderApi.queryGeoNeutronDiscoverFirst(geoSearchParams);
          
          if (geoneutronData && geoneutronData.total_devices > 0) {
            
            // Convert GeoNeutron devices to AreaSearchResult format
            const geoNeutronDeviceGroups = new Map<string, any[]>();
            
            // Group devices by registration ID
            geoneutronData.devices?.forEach((device: any) => {
              const regId = device.registration_id;
              if (!geoNeutronDeviceGroups.has(regId)) {
                geoNeutronDeviceGroups.set(regId, []);
              }
              geoNeutronDeviceGroups.get(regId)!.push(device);
            });
            
            // Create results for each unique device
            geoNeutronDeviceGroups.forEach((devicePoints, regId) => {
              if (devicePoints.length > 0) {
                const timestamps = devicePoints.map(p => new Date(p.timestamp).getTime());
                
                const result: AreaSearchResult = {
                  entityId: regId, // Prefix with GN to distinguish from internal data
                  entityType: 'External',
                  deviceType: 'phone', // Default to phone for GeoNeutron devices
                  pointCount: devicePoints.length,
                  timeRange: {
                    start: new Date(Math.min(...timestamps)),
                    end: new Date(Math.max(...timestamps))
                  },
                  confidence: 0.8, // Lower confidence for external data
                  dataPoints: devicePoints.map(p => ({
                    id: `${p.registration_id}-${p.timestamp}`,
                    studentId: p.registration_id,
                    entityId: p.registration_id,
                    deviceId: p.registration_id, // Add deviceId
                    courseId: '',
                    timestamp: new Date(p.timestamp),
                    latitude: p.latitude,
                    longitude: p.longitude,
                    coordinates: [p.latitude, p.longitude], // Internal format [lat, lng] - converted to GeoJSON [lng, lat] when rendering
                    accuracy: p.accuracy || 0,
                    deviceType: undefined, // Use undefined instead of 'unknown' for external devices
                    batteryLevel: null,
                    isConnected: true,
                    signalStrength: null,
                    dataSource: 'geoneutron',
                    metadata: {
                      ip_address: p.ip_address,
                      forensic_flag: p.forensic_flag,
                      country: p.country,
                      carrier: p.carrier,
                      timezone: p.timezone
                    }
                  }))
                };
                
                geoneutronResults.push(result);
              }
            });
            
          } else {
          }
        } catch (error) {
          // If GeoNeutron fails, results will be empty
        }

            // No internal PostgreSQL query - only GeoNeutron results
      const allResults = geoneutronResults; // Only GeoNeutron results
      
      if (geoneutronResults.length > 0) {
      }

            // Fetch device details (Explore API) for popup enrichment
      let exploreDeviceDetails: Record<string, any> = {};
      if (geoneutronData?.devices?.length > 0) {
        try {
          const regIds = Array.from(new Set(geoneutronData.devices.map((d: any) => d.registration_id))).filter(Boolean) as string[];
          if (regIds.length > 0) {
            const bulkRes = await geoneutronApi.getDevicesBulk(regIds);
            const devices = bulkRes?.devices ?? [];
            devices.forEach((d: any) => {
              const id = d.registrationID ?? d.registration_id;
              if (id) exploreDeviceDetails[id] = d;
            });
          }
        } catch {
          // Non-blocking
        }
      }

      // Update circle with results
      const completedCircle = {
        ...circle,
        loading: false,
        results: allResults.sort((a, b) => b.pointCount - a.pointCount),
        geoneutronData: geoneutronData,
        exploreDeviceDetails: Object.keys(exploreDeviceDetails).length > 0 ? exploreDeviceDetails : undefined,
      };

      onCircleUpdate(completedCircle);

    } catch (error) {
      // Update circle to show error state - keep results undefined so UI shows appropriate message
      const errorCircle = { ...circle, loading: false, error: true };
      onCircleUpdate(errorCircle);
    }
  }, [dateRange, searchMode, timeRange, onCircleUpdate]);

  // Analyze dwells in the area
  const analyzeDwells = useCallback(async (circle: DrawCircle) => {
    if (!dateRange.start || !dateRange.end) return;

    try {
      // Fetch dwells for the area
      // Backend will filter by entity_type if studentsOnly=true
      const dwells = await pathfinderApi.getDwells({
        courseId: selectedCourse,
        startDate: dateRange.start,
        endDate: dateRange.end,
        limit: 25000,  // High limit balanced with performance
        studentsOnly: studentsOnly  // Backend filters by entity_type='Student'
      });
      
      // Backend already filtered - no need for frontend filtering
      const filteredDwells = dwells;

      const dwellsInArea = filteredDwells.filter((dwell: any) => {
        const distance = calculateDistance(
          circle.center[0], circle.center[1],
          dwell.latitude, dwell.longitude
        );
        const distanceMeters = distance * 1000;
        
        // Log first 5 calculations to see what's happening
        if (filteredDwells.indexOf(dwell) < 5) {
        }
        
        return distanceMeters <= circle.radius;
      });


      // Group dwells by entity
      const dwellsByEntity = new Map<string, any[]>();
      dwellsInArea.forEach((dwell: any) => {
        const key = `${dwell.entity_id}-${dwell.device_type}`;
        if (!dwellsByEntity.has(key)) {
          dwellsByEntity.set(key, []);
        }
        dwellsByEntity.get(key)!.push(dwell);
      });

      // Calculate dwell insights
      const dwellInsights: DwellInsight[] = [];
      dwellsByEntity.forEach((dwells, entityKey) => {
        const totalDwellTime = dwells.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);
        const longestDwell = dwells.reduce((max, d) => 
          (d.duration_seconds || 0) > (max?.duration_seconds || 0) ? d : max
        , dwells[0]);

        // Split by last dash to separate entity ID from device type
        // e.g., "Student-06-phone" -> "Student-06" and "phone"
        const lastDashIndex = entityKey.lastIndexOf('-');
        const entityId = entityKey.substring(0, lastDashIndex);
        const deviceType = entityKey.substring(lastDashIndex + 1);
        
        dwellInsights.push({
          entityId,
          deviceType: deviceType || 'phone',
          dwellCount: dwells.length,
          totalDwellTime,
          longestDwell: {
            duration: longestDwell.duration_seconds || 0,
            startTime: new Date(longestDwell.start_time),
            endTime: new Date(longestDwell.end_time),
            placeName: longestDwell.place_name,
            placeType: longestDwell.place_type
          },
          coordinates: [longestDwell.longitude, longestDwell.latitude],  // GeoJSON: [lng, lat]
          placeName: longestDwell.place_name,
          placeType: longestDwell.place_type
        });
      });

      return dwellInsights.sort((a, b) => b.totalDwellTime - a.totalDwellTime);
    } catch (error) {
      return [];
    }
  }, [dateRange, selectedCourse, studentsOnly]);

  // Calculate distance using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Detect co-location events
  const detectCoLocations = useCallback(async (circle: DrawCircle) => {
    if (!circle.dwellInsights || circle.dwellInsights.length < 2) return [];

    const coLocations: CoLocationInsight[] = [];
    const dwellInsights = circle.dwellInsights;

    // Compare all pairs of entities
    for (let i = 0; i < dwellInsights.length; i++) {
      for (let j = i + 1; j < dwellInsights.length; j++) {
        const entity1 = dwellInsights[i];
        const entity2 = dwellInsights[j];

        // Check if their longest dwells overlap in time and space
        const time1Start = entity1.longestDwell.startTime.getTime();
        const time1End = entity1.longestDwell.endTime.getTime();
        const time2Start = entity2.longestDwell.startTime.getTime();
        const time2End = entity2.longestDwell.endTime.getTime();

        // Check temporal overlap
        const overlapStart = Math.max(time1Start, time2Start);
        const overlapEnd = Math.min(time1End, time2End);
        const overlapDuration = (overlapEnd - overlapStart) / 1000; // seconds

        if (overlapDuration > 60) { // At least 1 minute overlap
          // Check spatial proximity (within 100m)
          const distance = calculateDistance(
            entity1.coordinates[0], entity1.coordinates[1],
            entity2.coordinates[0], entity2.coordinates[1]
          );

          if (distance < 0.1) { // Within 100 meters
            // Use place info from whichever entity has it (prefer entity1)
            const placeName = entity1.placeName || entity2.placeName;
            const placeType = entity1.placeType || entity2.placeType;
            
            coLocations.push({
              entities: [entity1.entityId, entity2.entityId],
              deviceTypes: [entity1.deviceType, entity2.deviceType],
              overlapStart: new Date(overlapStart),
              overlapEnd: new Date(overlapEnd),
              duration: overlapDuration,
              location: [
                (entity1.coordinates[0] + entity2.coordinates[0]) / 2,
                (entity1.coordinates[1] + entity2.coordinates[1]) / 2
              ],
              confidence: Math.min(1, 1 - (distance / 0.1)), // Closer = higher confidence
              placeName,
              placeType
            });
          }
        }
      }
    }

    return coLocations.sort((a, b) => b.duration - a.duration);
  }, []);

  // Start full analysis with discovery popup first
  const startFullAnalysisWithDiscovery = useCallback(async (circle: DrawCircle) => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }

    // Validate circle data
    if (!circle.center || !Array.isArray(circle.center) || circle.center.length !== 2) {
      console.error('[Discovery] Invalid circle center:', circle);
      return;
    }

    // Determine time range
    let searchStartDate: Date;
    let searchEndDate: Date;
    
    if (searchMode === 'current-time' && timeRange) {
      const bufferMinutes = 30;
      searchStartDate = new Date(timeRange.start.getTime() - (bufferMinutes * 60 * 1000));
      searchEndDate = new Date(timeRange.end.getTime() + (bufferMinutes * 60 * 1000));
    } else {
      searchStartDate = dateRange.start;
      searchEndDate = dateRange.end;
    }

    // Check date range (GeoNeutron API has 91-day limit)
    const daysDiff = (searchEndDate.getTime() - searchStartDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff > 90) {
      searchStartDate = new Date(searchEndDate.getTime() - (90 * 24 * 60 * 60 * 1000));
    }

    // Build search params with validated data
    const geoSearchParams: any = {
      dateRange: [searchStartDate, searchEndDate],
      sampleDays: 3
    };
    
    if (circle.shapeType === 'polygon' && circle.coordinates) {
      geoSearchParams.polygons = [{
        id: circle.id,
        coordinates: circle.coordinates
      }];
    } else {
      // Ensure we have valid numbers for the area search
      const lat = Number(circle.center[0]);
      const lon = Number(circle.center[1]);
      const radius = Number(circle.radius);
      
      if (isNaN(lat) || isNaN(lon) || isNaN(radius)) {
        console.error('[Discovery] Invalid numeric values:', { lat, lon, radius, original: circle });
        return;
      }
      
      geoSearchParams.areas = [{
        center: [lat, lon] as [number, number],
        radius: Math.max(10, Math.round(radius)), // Ensure positive integer radius
        id: circle.id
      }];
    }

    // Open dialog in loading state
    setDiscoveryDialog({
      open: true,
      loading: true,
      circle,
      discovery: null,
      strategy: null,
      searchParams: geoSearchParams,
      flowType: 'full_analysis'
    });

    try {
      const discoveryResult = await pathfinderApi.discoverGeoNeutronDeviceCount(geoSearchParams);
      
      const totalDays = Math.ceil(daysDiff);
      const strategyName = discoveryResult.recommended_strategy?.name || 'temporal_partitioning';
      
      setDiscoveryDialog(prev => ({
        ...prev,
        loading: false,
        discovery: {
          deviceCount: discoveryResult.discovery?.estimated_total_devices || 0,
          signalCount: discoveryResult.discovery?.sample_signal_count || 0,
          totalDays
        },
        strategy: {
          name: strategyName,
          reason: discoveryResult.recommended_strategy?.reason || 'Standard partitioning',
          chunkDays: discoveryResult.recommended_strategy?.chunk_days || 5,
          maxConcurrent: discoveryResult.recommended_strategy?.max_concurrent || 4,
          estimatedTime: getEstimatedTime(
            strategyName
          )
        }
      }));
      
    } catch (error: any) {
      console.error('[FullAnalysis] Discovery failed:', error);
      
      // Show error in dialog instead of silently falling back
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      setDiscoveryDialog(prev => ({
        ...prev,
        loading: false,
        discovery: {
          deviceCount: -1, // Indicates error
          signalCount: 0,
          totalDays: Math.ceil(daysDiff)
        },
        strategy: {
          name: 'error',
          reason: `Discovery failed: ${errorMessage}. Try re-drawing the area or adjusting the date range.`,
          chunkDays: 3,
          maxConcurrent: 4,
          estimatedTime: 'Unknown'
        }
      }));
    }
  }, [dateRange, searchMode, timeRange]);

  // Main entry point for full analysis (with discovery popup)
  const performFullAnalysis = useCallback(async (circle: DrawCircle) => {
    // IMPORTANT: Get the latest circle data from drawCircles to ensure we have fresh coordinates
    // This prevents stale data after editing circles
    const freshCircle = drawCircles.find(c => c.id === circle.id) || circle;
    
    // Validate circle data before proceeding
    if (!freshCircle.center || !Array.isArray(freshCircle.center) || freshCircle.center.length !== 2) {
      console.error('[FullAnalysis] Invalid circle center:', freshCircle.center);
      alert('Invalid area coordinates. Please re-draw the area and try again.');
      return;
    }
    
    if (freshCircle.shapeType === 'circle' && (typeof freshCircle.radius !== 'number' || freshCircle.radius <= 0 || isNaN(freshCircle.radius))) {
      console.error('[FullAnalysis] Invalid circle radius:', freshCircle.radius);
      alert('Invalid area radius. Please re-draw the area and try again.');
      return;
    }
    
    // Validate coordinates are valid numbers
    const [lat, lon] = freshCircle.center;
    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      console.error('[FullAnalysis] Invalid coordinates:', { lat, lon });
      alert('Invalid area coordinates. Please re-draw the area and try again.');
      return;
    }
    
    console.log('[FullAnalysis] Using fresh circle data:', {
      id: freshCircle.id,
      center: freshCircle.center,
      radius: freshCircle.radius,
      shapeType: freshCircle.shapeType
    });
    
    // Use discovery popup to show device count before analyzing
    startFullAnalysisWithDiscovery(freshCircle);
  }, [startFullAnalysisWithDiscovery, drawCircles]);

  // Perform full analysis (search + dwells + co-location + AI) - direct execution
  const performFullAnalysisDirect = useCallback(async (circle: DrawCircle) => {
    
    // Set loading state
    onCircleUpdate({ ...circle, loading: true });
    
    try {
      // First, perform the area search to get GPS track points
      // We need to replicate the logic here to get the results directly
      if (!dateRange.start || !dateRange.end) {
        return;
      }

      let searchStartDate: Date;
      let searchEndDate: Date;
      
      if (searchMode === 'current-time' && timeRange) {
        const bufferMinutes = 30;
        searchStartDate = new Date(timeRange.start.getTime() - (bufferMinutes * 60 * 1000));
        searchEndDate = new Date(timeRange.end.getTime() + (bufferMinutes * 60 * 1000));
      } else {
        searchStartDate = dateRange.start;
        searchEndDate = dateRange.end;
      }

      // GeoNeutron area search (if enabled) - discover ALL devices in area
      let geoneutronData: any = null;
      let geoneutronResults: AreaSearchResult[] = [];
      if (useGeoNeutron) {
        try {
          
          // Check date range (GeoNeutron API has 91-day limit)
          const daysDiff = (searchEndDate.getTime() - searchStartDate.getTime()) / (1000 * 60 * 60 * 24);
          if (daysDiff > 90) {
            // Clamp to 90 days from end date
            searchStartDate = new Date(searchEndDate.getTime() - (90 * 24 * 60 * 60 * 1000));
          }
          
          // Cap end date to current time to prevent future dates (especially in UTC)
          const now = new Date();
          let adjustedEndDate = searchEndDate;
          
          // Check if the end date would be in the future when sent to API (in UTC)
          // This is important because local time 23:59:59 might be tomorrow in UTC
          if (searchEndDate > now) {
            adjustedEndDate = now;
          }
          
          
          // Call GeoNeutron DISCOVER-FIRST API for optimal performance
          const geoSearchParams: any = {
            dateRange: [searchStartDate, adjustedEndDate],
            registrationIds: [], // Empty array = search ALL devices in area
            sampleDays: 3 // For discovery phase
          };
          
          // Use polygon if available, otherwise use circle parameters
          if (circle.shapeType === 'polygon' && circle.coordinates) {
            geoSearchParams.polygons = [{
              id: circle.id,
              coordinates: circle.coordinates
            }];
          } else {
            geoSearchParams.areas = [{
              center: circle.center,
              radius: circle.radius,
              id: circle.id
            }];
          }
          
          geoneutronData = await pathfinderApi.queryGeoNeutronDiscoverFirst(geoSearchParams);
          
          if (geoneutronData && geoneutronData.total_devices > 0) {
            
            // Convert GeoNeutron devices to AreaSearchResult format
            const geoNeutronDeviceGroups = new Map<string, any[]>();
            
            // Group devices by registration ID
            geoneutronData.devices?.forEach((device: any) => {
              const regId = device.registration_id;
              if (!geoNeutronDeviceGroups.has(regId)) {
                geoNeutronDeviceGroups.set(regId, []);
              }
              geoNeutronDeviceGroups.get(regId)!.push(device);
            });
            
            // Create results for each registration ID
            geoNeutronDeviceGroups.forEach((devicePoints, regId) => {
              if (devicePoints.length > 0) {
                const timestamps = devicePoints.map(p => new Date(p.timestamp).getTime());
                
                const result: AreaSearchResult = {
                  entityId: `${regId}`,
                  entityType: 'External',
                  deviceType: 'phone', // Default to phone for GeoNeutron devices
                  pointCount: devicePoints.length,
                  timeRange: {
                    start: new Date(Math.min(...timestamps)),
                    end: new Date(Math.max(...timestamps))
                  },
                  confidence: 0.85, // Lower confidence for external data
                  dataPoints: devicePoints.map(p => ({
                    id: `${p.registration_id}-${p.timestamp}`,
                    studentId:p.registration_id,
                    entityId: p.registration_id,
                    deviceId: p.registration_id,
                    courseId: '',
                    timestamp: new Date(p.timestamp),
                    latitude: p.latitude,
                    longitude: p.longitude,
                    coordinates: [p.latitude, p.longitude],  // Internal format: [lat, lng]
                    accuracy: p.accuracy || 0,
                    deviceType: undefined,
                    batteryLevel: null,
                    isConnected: true,
                    signalStrength: null,
                    dataSource: 'geoneutron',
                    metadata: {
                      ip_address: p.ip_address,
                      forensic_flag: p.forensic_flag,
                      country: p.country,
                      carrier: p.carrier,
                      timezone: p.timezone
                    }
                  }))
                };
                geoneutronResults.push(result);
              }
            });
            
          }
        } catch {
          // Continue with internal data even if GeoNeutron fails
        }
      }

            // Fetch device details (Explore API) for popup enrichment
      let exploreDeviceDetails: Record<string, any> = {};
      if (geoneutronData?.devices?.length > 0) {
        try {
          const regIds = Array.from(new Set(geoneutronData.devices.map((d: any) => d.registration_id))).filter(Boolean) as string[];
          if (regIds.length > 0) {
            const bulkRes = await geoneutronApi.getDevicesBulk(regIds);
            const devices = bulkRes?.devices ?? [];
            devices.forEach((d: any) => {
              const id = d.registrationID ?? d.registration_id;
              if (id) exploreDeviceDetails[id] = d;
            });
          }
        } catch {
          // Non-blocking
        }
      }

      // Merge all results
      
      // Dwells temporarily disabled until hooked up to new database
      const dwellInsights: DwellInsight[] = [];
      
      // Detect co-locations
      const coLocationInsights = await detectCoLocations({ ...circle, dwellInsights });
      
      // Skip automatic AI generation - user can manually request it via the "Generate AI Insights" button
      // This saves ~34 seconds per area search
      
      // Final update with ALL data preserved
      const finalCircle: DrawCircle = {
        ...circle,
        results: geoneutronResults.sort((a, b) => b.pointCount - a.pointCount), // Preserve GPS track results
        dwellInsights,
        coLocationInsights,
        aiSummary: null, // AI is now generated on-demand
        mapboxContext: null,
        geoneutronData, // Store raw GeoNeutron data for reference
        exploreDeviceDetails: Object.keys(exploreDeviceDetails).length > 0 ? exploreDeviceDetails : undefined,
        loading: false
      };
      
      onCircleUpdate(finalCircle);
      
            // Auto-load data to map after analysis completes
      const allDataPoints: PathfinderDataPoint[] = [];
      finalCircle.results?.forEach(result => {
        if (result.dataPoints) allDataPoints.push(...result.dataPoints);
      });

      const dwells = finalCircle.dwellInsights || [];
      
      // Load GPS points and GeoNeutron data
      if (onLoadAreaData && allDataPoints.length > 0) {
        const geoNeutronDevices = finalCircle.geoneutronData?.devices || [];
        const circleInfo = {
          center: finalCircle.center,
          radius: finalCircle.radius
        };
        onLoadAreaData(allDataPoints, geoNeutronDevices, circleInfo, finalCircle.exploreDeviceDetails);
      }
      
      // Load dwells
      if (onLoadAreaDwells && dwells.length > 0) {
        onLoadAreaDwells(dwells);
      }
      
    } catch (error) {
      onCircleUpdate({ ...circle, loading: false });
    }
  }, [analyzeDwells, detectCoLocations, onCircleUpdate, dateRange, searchMode, timeRange, selectedCourse, studentsOnly, useGeoNeutron]);

  // Toggle circle visibility
  const toggleCircleVisibility = useCallback((circle: DrawCircle) => {
    const updatedCircle = { ...circle, visible: !circle.visible };
    onCircleUpdate(updatedCircle);
  }, [onCircleUpdate]);

  // Perform intersection analysis across multiple areas
  const performIntersectionAnalysis = useCallback(async () => {
    if (drawCircles.length < 2) {
      alert('Please select at least 2 areas to find common devices.');
      return;
    }

    if (!dateRange.start || !dateRange.end) {
      alert('Please select a date range first.');
      return;
    }

    // Set loading state
    setIsAnalyzing(true);
    
    // Set all circles to loading
    drawCircles.forEach(circle => onCircleUpdate({ ...circle, loading: true }));

    try {
      // Prepare date range
      let searchStartDate: Date = dateRange.start!;
      let searchEndDate: Date = dateRange.end!;

      if (searchMode === 'current-time' && timeRange) {
        const bufferMinutes = 30;
        searchStartDate = new Date(timeRange.start.getTime() - (bufferMinutes * 60 * 1000));
        searchEndDate = new Date(timeRange.end.getTime() + (bufferMinutes * 60 * 1000));
      }

      // Cap end date to current time
      const now = new Date();
      if (searchEndDate > now) {
        searchEndDate = now;
      }

      // Check date range (GeoNeutron API has 91-day limit)
      const rangeDays = (searchEndDate.getTime() - searchStartDate.getTime()) / (1000 * 60 * 60 * 24);
      if (rangeDays > 90) {
        // Clamp to 90 days from end date
        searchStartDate = new Date(searchEndDate.getTime() - (90 * 24 * 60 * 60 * 1000));
        alert(`Date range exceeds 90-day limit.\n\nThe GeoNeutron API only allows searches up to 90 days.\n\nAutomatically adjusted start date to: ${searchStartDate.toLocaleDateString()}`);
      }

      // Separate circles and polygons for the API
      const circleAreas = drawCircles
        .filter(c => c.shapeType === 'circle' || !c.coordinates)
        .map(circle => ({
          center: circle.center,
          radius: circle.radius,
          id: circle.id
        }));

      const polygonAreas = drawCircles
        .filter(c => c.shapeType === 'polygon' && c.coordinates)
        .map(circle => ({
          id: circle.id,
          coordinates: circle.coordinates! // Already in GeoJSON format [[[lng, lat], ...]]
        }));


      // Use the new multi-area API endpoint
      const geoneutronData = await pathfinderApi.queryGeoNeutronMultiAreaSearch({
        areas: circleAreas,
        polygons: polygonAreas.length > 0 ? polygonAreas : undefined,
        dateRange: [searchStartDate, searchEndDate],
        useAndLogic: analysisMode === 'intersection', // KEY: Use AND logic for intersection
        registrationIds: []
      });


      if (geoneutronData.total_devices === 0) {
        const message = analysisMode === 'intersection'
          ? `No common devices found.\n\nSearched ${drawCircles.length} areas but found no devices that appear in all of them.\n\nTry:\n- Expanding the search radius\n- Using a longer date range\n- Checking different time periods\n- Or switch to "All" mode to see all devices`
          : `No devices found.\n\nSearched ${drawCircles.length} areas but found no devices.\n\nTry:\n- Expanding the search radius\n- Using a longer date range\n- Checking different time periods`;
        
        // Clear loading states
        setIsAnalyzing(false);
        drawCircles.forEach(circle => onCircleUpdate({ ...circle, loading: false }));
        alert(message);
        return;
      }

      // Process the API results - the API already did the filtering!
      const geoNeutronDeviceGroups = new Map<string, any[]>();

      geoneutronData.devices?.forEach((device: any) => {
        const regId = device.registration_id;
        if (!geoNeutronDeviceGroups.has(regId)) {
          geoNeutronDeviceGroups.set(regId, []);
        }
        geoNeutronDeviceGroups.get(regId)!.push(device);
      });

      // Create results array
      const filteredResults: AreaSearchResult[] = [];
      geoNeutronDeviceGroups.forEach((devicePoints, regId) => {
        if (devicePoints.length > 0) {
          const timestamps = devicePoints.map(p => new Date(p.timestamp).getTime());
          filteredResults.push({
            entityId: regId,
            entityType: 'External',
            deviceType: 'phone',
            pointCount: devicePoints.length,
            timeRange: {
              start: new Date(Math.min(...timestamps)),
              end: new Date(Math.max(...timestamps))
            },
            confidence: analysisMode === 'intersection' ? 0.95 : 0.85,
            dataPoints: devicePoints.map(p => ({
              id: `${p.registration_id}-${p.timestamp}`,
              studentId: p.registration_id,
              deviceId: p.registration_id,
              entityId: p.registration_id,
              entityType: 'External',
              deviceType: 'phone',
              timestamp: new Date(p.timestamp),
              coordinates: [p.latitude, p.longitude],  // Internal format: [lat, lng]
              latitude: p.latitude,
              longitude: p.longitude,
              accuracy: p.accuracy || 50,
              speed: 0,
              heading: 0,
              source: 'geoneutron',
              metadata: {
                ip_address: p.ip_address,
                forensic_flag: p.forensic_flag,
                country: p.country,
                carrier: p.carrier,
                timezone: p.timezone
              }
            }))
          });
        }
      });

      // Create a virtual result circle
      const centerLat = drawCircles.reduce((sum, c) => sum + c.center[0], 0) / drawCircles.length;
      const centerLon = drawCircles.reduce((sum, c) => sum + c.center[1], 0) / drawCircles.length;

      const resultCircle: DrawCircle = {
        id: `${analysisMode}-${Date.now()}`,
        shapeType: 'circle',
        center: [centerLat, centerLon],
        radius: 1000,
        name: analysisMode === 'intersection' 
          ? `Common Devices (${geoneutronData.unique_registration_ids})` 
          : `All Devices (${geoneutronData.unique_registration_ids})`,
        visible: true,
        results: filteredResults,
        loading: false,
        isResultCircle: true,
        geoneutronData: geoneutronData,
      };

      // Clear loading states and mark as participated in common analysis
      drawCircles.forEach(circle => onCircleUpdate({ 
        ...circle, 
        loading: false,
        participatedInCommonAnalysis: true // Mark that this circle was part of common analysis
      }));

      // Add the result circle
      onCircleAdd(resultCircle);

      // Auto-load the data to the map immediately after analysis completes
      if (onLoadAreaData && filteredResults.length > 0) {
        const allDataPoints: PathfinderDataPoint[] = [];
        filteredResults.forEach(result => {
          if (result.dataPoints) allDataPoints.push(...result.dataPoints);
        });
        
        const geoNeutronDevices = geoneutronData.devices || [];
        const circleInfo = {
          center: resultCircle.center,
          radius: resultCircle.radius
        };
        onLoadAreaData(allDataPoints, geoNeutronDevices, circleInfo);
      }

      const message = analysisMode === 'intersection'
        ? `Intersection Analysis Complete!\n\nFound ${geoneutronData.unique_registration_ids} devices appearing in ALL ${drawCircles.length} areas.\n\n${geoneutronData.total_devices} total location points.\n\nData automatically loaded to map.`
        : `Union Analysis Complete!\n\nFound ${geoneutronData.unique_registration_ids} total unique devices across ALL ${drawCircles.length} areas.\n\n${geoneutronData.total_devices} total location points.\n\nData automatically loaded to map.`;
      
      setIsAnalyzing(false);
      alert(message);

    } catch (error: any) {
      
      // Clear loading states
      setIsAnalyzing(false);
      drawCircles.forEach(circle => onCircleUpdate({ ...circle, loading: false }));
      
      // Provide helpful error message
      if (error.message?.includes('timeout')) {
        const daysDiff = Math.ceil((dateRange.end!.getTime() - dateRange.start!.getTime()) / (1000 * 60 * 60 * 24));
        alert(`Search Timeout\n\nThe search took too long (searching ${drawCircles.length} areas over ${daysDiff} days).\n\nTry:\n- Reducing the date range to 3-5 days\n- Searching fewer areas at once\n- Using smaller radius circles\n\nThe BS Discover API has limits on search complexity.`);
      } else {
        alert(`Analysis failed: ${error.message || error}\n\nCheck the console for more details.`);
      }
    }
  }, [drawCircles, dateRange, searchMode, timeRange, onCircleUpdate, onCircleAdd, analysisMode, onLoadAreaData]);

  const handleDeleteDevice = async (deviceId: number, name: string) => {
    if (window.confirm(`Remove "${name}" from watchlist?`)) {
      try {
        await pathfinderApi.removeFromWatchlist(deviceId);
        // Refresh the list
        window.dispatchEvent(new CustomEvent('refresh-watchlist'));
        // Remove from selection if selected
        setSelectedDeviceIds(prev => prev.filter(id => id !== deviceId));
      } catch {
        alert('Failed to delete device. Please try again.');
      }
    }
  };

  return (
    <Box 
      ref={scrollContainerRef}
      sx={{ 
        p: 1.5, 
        pb: 8, // Extra padding at bottom to ensure content isn't hidden behind overlays
        flex: 1,
        minHeight: 0, // Critical: allows proper shrinking on scaled displays (125%+)
        height: '100%', 
        overflow: 'auto',
        overflowX: 'hidden',
        overflowAnchor: 'none', // Prevent browser scroll anchoring from snapping back
        display: 'block', // Use block instead of flex to prevent layout recalculations
      }}
    >
      {/* Date Range Selection */}
      <Box sx={{ 
        mb: 1.5, 
        p: 1.5, 
        bgcolor: 'background.paper', 
        borderRadius: 1, 
        border: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        maxWidth: '100%'
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Date Range Filter
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={dateRange.start ? formatDateForInput(dateRange.start) : ''}
            onChange={(e) => {
              if (e.target.value) {
                const [year, month, day] = e.target.value.split('-').map(Number);
                const newStart = new Date(year, month - 1, day);
                // If we have an end date, validate before updating
                if (dateRange.end) {
                  if (newStart <= dateRange.end) {
                    onDateRangeChange?.({ start: newStart, end: dateRange.end });
                  }
                } else {
                  // No end date yet - set a default end date to today
                  const defaultEnd = new Date();
                  defaultEnd.setHours(23, 59, 59, 999);
                  onDateRangeChange?.({ start: newStart, end: defaultEnd });
                }
              }
            }}
            onClick={(e) => {
              // Make the entire field clickable to open the date picker
              const input = e.currentTarget.querySelector('input');
              if (input) {
                input.showPicker?.();
              }
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ 
              flex: '1 1 140px', 
              minWidth: 0, 
              maxWidth: '100%',
              cursor: 'pointer',
              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: 'invert(1)',
                cursor: 'pointer',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1
                }
              }
            }}
            helperText="Filter start date"
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={dateRange.end ? formatDateForInput(dateRange.end) : ''}
            onChange={(e) => {
              if (e.target.value) {
                const [year, month, day] = e.target.value.split('-').map(Number);
                const newEnd = new Date(year, month - 1, day);
                newEnd.setHours(23, 59, 59, 999);
                // If we have a start date, validate before updating
                if (dateRange.start) {
                  if (newEnd >= dateRange.start) {
                    onDateRangeChange?.({ start: dateRange.start, end: newEnd });
                  }
                } else {
                  // No start date yet - set a default start date (30 days before end)
                  const defaultStart = new Date(newEnd);
                  defaultStart.setDate(defaultStart.getDate() - 30);
                  defaultStart.setHours(0, 0, 0, 0);
                  onDateRangeChange?.({ start: defaultStart, end: newEnd });
                }
              }
            }}
            onClick={(e) => {
              // Make the entire field clickable to open the date picker
              const input = e.currentTarget.querySelector('input');
              if (input) {
                input.showPicker?.();
              }
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ 
              flex: '1 1 140px', 
              minWidth: 0, 
              maxWidth: '100%',
              cursor: 'pointer',
              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: 'invert(1)',
                cursor: 'pointer',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1
                }
              }
            }}
            helperText="Filter end date"
          />
        </Box>
      </Box>

      {/* ID/IP Lookup Search */}
      <Box sx={{ 
        mb: 1.5, 
        p: 1.5, 
        bgcolor: 'background.paper', 
        borderRadius: 1, 
        border: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        maxWidth: '100%'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            ID / IP Lookup
          </Typography>
          <Tooltip 
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Search by Registration ID or IP Address</Typography>
                <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                  <strong>Registration ID:</strong> Returns full device data including country, carrier, timezone, and forensic flags.
                </Typography>
                <Typography variant="caption" component="div">
                  <strong>IP Address:</strong> Links IPs to registration IDs and locations. May have limited metadata (country, carrier, timezone may not be available).
                </Typography>
              </Box>
            }
            arrow
            placement="right"
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="Registration IDs or IP Addresses"
            placeholder="e.g., ABC123, XYZ789 or 192.168.1.1, 10.0.0.1"
            size="small"
            value={idIpSearchInput}
            onChange={(e) => setIdIpSearchInput(e.target.value)}
            sx={{ flex: 1, minWidth: 0 }}
            helperText="Enter comma-separated values"
            disabled={idIpSearchLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                performIdIpSearch();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={performIdIpSearch}
            disabled={idIpSearchLoading || !idIpSearchInput.trim()}
            startIcon={idIpSearchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
            sx={{ height: 40, minWidth: 100 }}
          >
            {idIpSearchLoading ? 'Searching...' : 'Search'}
          </Button>
        </Box>
        
        {/* Error Display */}
        {idIpSearchError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setIdIpSearchError(null)}>
            {idIpSearchError}
          </Alert>
        )}
        
        {/* Results Summary */}
        {idIpSearchResults && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            {(() => {
              const locationCount = idIpSearchResults.locations?.length || idIpSearchResults.locationData?.length || idIpSearchResults.signals?.length || 0;
              if (locationCount > 0) {
                return (
                  <>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                      Found {locationCount} location records
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Data has been loaded to the map
                    </Typography>
                  </>
                );
              } else {
                return (
                  <>
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                      No location data found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The ID/IP may not have data in the selected date range, or may not exist in the system.
                    </Typography>
                  </>
                );
              }
            })()}
          </Box>
        )}

        {/* Saved Devices - Under ID/IP Lookup */}
        {savedDevices.length > 0 && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'secondary.light', borderRadius: 1, border: '1px solid', borderColor: 'secondary.main' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'secondary.dark' }}>
                Saved Devices ({savedDevices.length})
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<FolderOpenIcon />}
                onClick={() => setShowDeviceManager(true)}
                sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75 }}
              >
                Select & Load
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Geographic Access Control Indicator */}
      {geographicAccess?.enabled && !geographicAccess.worldwide_access && (
        <Alert 
          severity={geographicAccess.allowed_regions.length > 0 ? "info" : "warning"}
          sx={{ mb: 1.5, py: 0.5 }}
          icon={<LocationIcon fontSize="small" />}
        >
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {geographicAccess.allowed_regions.length > 0 ? (
              <>
                🌍 <strong>Geographic Access:</strong> {geographicAccess.allowed_regions.map(r => r.name).join(', ')}
                {geographicAccess.max_query_radius_meters > 0 && (
                  <> • Max radius: {(geographicAccess.max_query_radius_meters / 1000).toFixed(0)}km</>
                )}
              </>
            ) : (
              <>⚠️ No geographic access configured. Contact your administrator.</>
            )}
          </Typography>
        </Alert>
      )}

      {/* Compact Drawing Controls */}
      <Box sx={{ 
        mb: 1.5, 
        p: 1.5, 
        bgcolor: 'background.paper', 
        borderRadius: 1, 
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flexShrink: 0,
        maxWidth: '100%'
      }}>
        {/* Row 1: Draw Controls */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: '100%' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Drawing Mode Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Drawing Tool:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  variant={isDrawingMode && drawingShapeType === 'circle' ? "contained" : "outlined"}
                  onClick={() => {
                    if (!isDrawingMode) {
                      onDrawModeChange(true, 'circle');
                    } else if (drawingShapeType !== 'circle') {
                      onDrawModeChange(true, 'circle');
                    }
                  }}
                  startIcon={<CircleIcon />}
                  size="small"
                  color={isDrawingMode && drawingShapeType === 'circle' ? "primary" : "inherit"}
                  sx={{ minWidth: 90 }}
                >
                  Circle
                </Button>
                <Button
                  variant={isDrawingMode && drawingShapeType === 'polygon' ? "contained" : "outlined"}
                  onClick={() => {
                    if (!isDrawingMode) {
                      onDrawModeChange(true, 'polygon');
                    } else if (drawingShapeType !== 'polygon') {
                      onDrawModeChange(true, 'polygon');
                    }
                  }}
                  startIcon={<TimelineIcon />}
                  size="small"
                  color={isDrawingMode && drawingShapeType === 'polygon' ? "primary" : "inherit"}
                  sx={{ minWidth: 90 }}
                >
                  Polygon
                </Button>
              </Box>
            </Box>
            
            {/* Radius Input (Aligned) */}
            {drawingShapeType === 'circle' && (
              <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Radius ({measurementSystem === 'metric' ? 'm' : 'ft'}):
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={measurementSystem === 'metric' ? Math.round(defaultRadius) : Math.round(metersToFeet(defaultRadius))}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    const meters = measurementSystem === 'metric' ? val : feetToMeters(val);
                    onRadiusChange?.(meters);
                  }}
                  sx={{ width: 120 }}
                  inputProps={{ min: 10 }}
                />
              </Box>
            )}

            {/* Measurement System Toggle */}
            <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', ml: 'auto' }}>
               <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  System:
               </Typography>
               <Select
                 value={measurementSystem}
                 onChange={(e) => onMeasurementSystemChange?.(e.target.value as 'metric' | 'imperial')}
                 size="small"
                 sx={{ width: 100, height: 40 }}
               >
                 <MenuItem value="metric">Metric</MenuItem>
                 <MenuItem value="imperial">Imperial</MenuItem>
               </Select>
            </Box>
          </Box>

          {isDrawingMode && (
            <Button
              variant="outlined"
              onClick={() => onDrawModeChange(false)}
              size="small"
              color="error"
              sx={{ alignSelf: 'flex-start' }}
            >
              Cancel Drawing
            </Button>
          )}

          {isDrawingMode && (
            <Typography variant="caption" sx={{ 
              color: 'primary.main', 
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              mt: 2,
            }}>
              {drawingShapeType === 'circle' 
                ? 'Click on map to place circle' 
                : 'Click map to draw polygon (double-click to finish)'}
            </Typography>
          )}
        </Box>

        {/* Saved Geofences - Under Drawing Controls */}
        {savedGeofences.length > 0 && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                Saved Geofences ({savedGeofences.length})
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<FolderOpenIcon />}
                onClick={() => setShowGeofenceManager(true)}
                sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75 }}
              >
                Select & Load
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Drawn Circles List */}
      <Card sx={{ flexShrink: 0, minHeight: 80, maxWidth: '100%' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, maxWidth: '100%', boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, overflow: 'hidden', maxWidth: '100%', flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
              Search Areas ({drawCircles.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              {drawCircles.length > 1 && (
                <>
                  <Tooltip title={analysisMode === 'intersection' ? 'Find devices in ALL areas' : 'Find ALL devices across areas'}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={performIntersectionAnalysis}
                      disabled={isAnalyzing || drawCircles.some(c => c.loading)}
                      startIcon={isAnalyzing ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {isAnalyzing 
                        ? 'Searching...' 
                        : (analysisMode === 'intersection' ? '🎯 Find Common' : '📊 Find All')}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Toggle between finding common devices or all devices">
                    <ButtonGroup size="small" sx={{ height: 36 }}>
                      <Button
                        variant={analysisMode === 'intersection' ? 'contained' : 'outlined'}
                        onClick={() => setAnalysisMode('intersection')}
                        sx={{ fontSize: '0.65rem', px: 1 }}
                      >
                        Common
                      </Button>
                      <Button
                        variant={analysisMode === 'union' ? 'contained' : 'outlined'}
                        onClick={() => setAnalysisMode('union')}
                        sx={{ fontSize: '0.65rem', px: 1 }}
                      >
                        All
                      </Button>
                    </ButtonGroup>
                  </Tooltip>
                </>
              )}
              {drawCircles.length > 0 && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={() => drawCircles.forEach(c => onCircleDelete(c.id))}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          {drawCircles.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No search areas drawn. Click "Draw Circle" to create one.
            </Typography>
          ) : (
            <List dense sx={{ maxWidth: '100%', pb: 0 }}>
              {drawCircles.map((circle) => (
                <ListItem key={circle.id} sx={{ 
                  px: 0,
                  overflow: 'hidden',
                  maxWidth: '100%',
                  ...(circle.isResultCircle ? {
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    p: 1,
                    mb: 1
                  } : {})
                }}>
                  <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', minWidth: 0, boxSizing: 'border-box' }}>
                    
                    {/* ========== RESULT CIRCLE - Compact View ========== */}
                    {circle.isResultCircle ? (
                      <>
                        {/* Compact header with count */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            ✓ {circle.name}
                          </Typography>
                          <Chip 
                            label={`${circle.geoneutronData?.unique_registration_ids || circle.results?.length || 0} devices`}
                            size="small"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        
                        {/* Action Buttons - Side by Side */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          sx={{ flex: 1 }}
                          startIcon={circle.loading ? <CircularProgress size={14} /> : <LocationIcon sx={{ fontSize: 16 }} />}
                          onClick={async () => {
                            // Get device IDs from geoneutronData (primary) or results (fallback)
                            let regIds: string[] = [];
                            
                            // Try to get from geoneutronData.devices first (has raw API data)
                            if (circle.geoneutronData?.devices && circle.geoneutronData.devices.length > 0) {
                              const uniqueRegIds = new Set<string>();
                              circle.geoneutronData.devices.forEach((d: any) => {
                                if (d.registration_id) uniqueRegIds.add(d.registration_id);
                              });
                              regIds = Array.from(uniqueRegIds);
                            }
                            
                            // Fallback to results array
                            if (regIds.length === 0 && circle.results) {
                              regIds = circle.results
                                .filter(r => r.entityType === 'External')
                                .map(r => r.entityId.replace('GN-', ''));
                            }
                            
                            if (regIds.length === 0) return;
                            
                            try {
                              onCircleUpdate({ ...circle, loading: true });
                              
                              // Calculate date range
                              let startDate: Date;
                              let endDate: Date;
                              
                              if (searchMode === 'current-time' && timeRange) {
                                const bufferHours = 24;
                                startDate = new Date(timeRange.start.getTime() - (bufferHours * 60 * 60 * 1000));
                                endDate = new Date(timeRange.end.getTime() + (bufferHours * 60 * 60 * 1000));
                              } else {
                                startDate = dateRange.start!;
                                endDate = dateRange.end!;
                              }
                              
                              const now = new Date();
                              if (endDate > now) endDate = now;
                              
                              // Batch requests (100 per batch)
                              const BATCH_SIZE = 100;
                              const batches = [];
                              for (let i = 0; i < regIds.length; i += BATCH_SIZE) {
                                batches.push(regIds.slice(i, i + BATCH_SIZE));
                              }
                              
                              const allDataPoints: PathfinderDataPoint[] = [];
                              let successfulBatches = 0;
                              
                              for (let i = 0; i < batches.length; i++) {
                                const batch = batches[i];
                                try {
                                  const patternData = await pathfinderApi.getGeoNeutronPatternOfLife({
                                    registrationIds: batch,
                                    startDate: startDate.toISOString().replace(/\.\d{3}Z$/, 'Z'),
                                    endDate: endDate.toISOString().replace(/\.\d{3}Z$/, 'Z')
                                  });
                                  
                                  for (const [registrationId, locations] of Object.entries(patternData.devices || {})) {
                                    const devicePoints = (locations as any[]).map((loc: any, index: number) => ({
                                      id: `gn-pol-${registrationId}-${index}`,
                                      deviceId: `GN-${registrationId}`,
                                      studentId: `GN-${registrationId}`,
                                      coordinates: [loc.latitude, loc.longitude] as [number, number], // Internal format: [lat, lng]
                                      timestamp: new Date(loc.timestamp),
                                      accuracy: loc.accuracy || 0,
                                      deviceType: 'phone' as const,
                                      metadata: { 
                                        isGeoNeutron: true,
                                        ip_address: loc.ip_address,
                                        forensic_flag: loc.forensic_flag,
                                        country: loc.country,
                                        carrier: loc.carrier,
                                        timezone: loc.timezone,
                                        altitude: loc.altitude,
                                        speed: loc.speed,
                                        bearing: loc.bearing
                                      }
                                    }));
                                    allDataPoints.push(...devicePoints);
                                  }
                                  successfulBatches++;
                                } catch (batchError) {
                                  console.error(`Batch ${i + 1} failed:`, batchError);
                                }
                              }
                              
                              onCircleUpdate({ ...circle, loading: false });
                              
                              if (allDataPoints.length > 0) {
                                const visiblePoints = allDataPoints.filter(point => !hiddenDevices.has(`GN-${point.deviceId}`));
                                const uniqueDeviceIds = Array.from(new Set(visiblePoints.map(p => p.deviceId)));
                                
                                if (onLoadPoLData && visiblePoints.length > 0) {
                                  onLoadPoLData(visiblePoints, { center: circle.center, radius: circle.radius }, uniqueDeviceIds.join(','));
                                }
                                
                                alert(`Loaded ${visiblePoints.length} location points for ${uniqueDeviceIds.length} devices!`);
                              } else if (successfulBatches === 0) {
                                alert(`All batches failed. The API may have timed out.\n\nTry:\n- Reducing the date range\n- Searching fewer devices at once`);
                              } else {
                                alert(`No Pattern of Life data found for the selected time range.\n\n${successfulBatches}/${batches.length} batches completed successfully.`);
                              }
                            } catch (error: any) {
                              onCircleUpdate({ ...circle, loading: false });
                              if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                                alert(`Request timed out.\n\nThe Pattern of Life search took too long.\n\nTry:\n- Reducing the date range\n- Searching fewer devices at once`);
                              } else {
                                alert(`Failed to load Pattern of Life: ${error?.message || error}`);
                              }
                            }
                          }}
                          disabled={circle.loading}
                        >
                          {circle.loading ? 'Loading...' : 'Load PoL'}
                        </Button>
                        
                        {/* Dismiss button */}
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                          onClick={() => onCircleDelete(circle.id)}
                          sx={{ opacity: 0.7, minWidth: 'auto', px: 1.5 }}
                        >
                          Dismiss
                        </Button>
                        </Box>
                      </>
                    ) : (
                      /* ========== REGULAR SEARCH AREA ========== */
                      <>
                    {/* Circle Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold', 
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0
                      }}>
                        {circle.name}
                      </Typography>
                      <Box>
                        {onEditingCircleChange && (
                          <Tooltip title={
                            editingCircleId === circle.id 
                              ? "Exit edit mode" 
                              : circle.shapeType === 'circle' 
                                ? "Edit circle (move/resize)" 
                                : "Edit polygon (drag vertices)"
                          }>
                            <IconButton 
                              size="small" 
                              onClick={() => onEditingCircleChange(editingCircleId === circle.id ? null : circle.id)}
                              color={editingCircleId === circle.id ? 'primary' : 'default'}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={circle.visible ? "Hide on map" : "Show on map"}>
                          <IconButton size="small" onClick={() => toggleCircleVisibility(circle)}>
                            {circle.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete circle">
                          <IconButton size="small" onClick={() => onCircleDelete(circle.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Circle Details with Geographic Context */}
                    {circle.mapboxContext?.place_name ? (
                      <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: 'primary.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {circle.mapboxContext.place_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {circle.center[0].toFixed(6)}, {circle.center[1].toFixed(6)} • Radius: {circle.radius}m
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Center: {circle.center[0].toFixed(6)}, {circle.center[1].toFixed(6)} | 
                        Radius: {circle.radius}m
                      </Typography>
                    )}

                    {/* Geographic Access Warning */}
                    {geographicAccess?.enabled && !geographicAccess.worldwide_access && (() => {
                      const accessStatus = getCircleAccessStatus(circle);
                      if (!accessStatus.allowed) {
                        return (
                          <Alert severity="error" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                            <Typography variant="caption">
                              {accessStatus.message}
                            </Typography>
                          </Alert>
                        );
                      }
                      return null;
                    })()}

                    {/* Action Buttons - Only show individual analyze when there's 1 area */}
                    <Box sx={{ mt: 1, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                      {/* Only show individual Analyze button if there's just 1 area */}
                      {drawCircles.filter(c => !c.isResultCircle).length === 1 && (
                        <Tooltip title={
                          geographicAccess?.enabled && !geographicAccess.worldwide_access && !getCircleAccessStatus(circle).allowed
                            ? getCircleAccessStatus(circle).message
                            : ''
                        }>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={circle.loading ? <CircularProgress size={16} /> : <SearchIcon />}
                              onClick={() => performFullAnalysis(circle)}
                              disabled={
                                circle.loading || 
                                !dateRange.start || 
                                !dateRange.end ||
                                (geographicAccess?.enabled && !geographicAccess.worldwide_access && !getCircleAccessStatus(circle).allowed)
                              }
                            >
                              {circle.loading ? 'Analyzing...' : 'Analyze Area'}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {!circle.geofenceId && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={savingGeofence === circle.id ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={() => openGeofenceNameDialog(circle)}
                          disabled={savingGeofence === circle.id}
                        >
                          {savingGeofence === circle.id ? 'Saving...' : 'Save Geofence'}
                        </Button>
                      )}
                    </Box>

                    {/* Compact Results Summary (no tabs) */}
                    {circle.results && circle.results.length > 0 && !circle.loading && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', display: 'block', mb: 1 }}>
                          {circle.results.length} Entities Found
                          {circle.geoneutronData?.unique_registration_ids > 0 && (
                            <span style={{ color: theme.palette.text.secondary, marginLeft: '8px' }}>
                              ({circle.geoneutronData.unique_registration_ids} Tracer API)
                            </span>
                          )}
                        </Typography>
                        
                        {/* Quick Stats */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {circle.dwellInsights && circle.dwellInsights.length > 0 && (
                            <Chip label={`${circle.dwellInsights.length} Dwells`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                          {circle.coLocationInsights && circle.coLocationInsights.length > 0 && (
                            <Chip label={`${circle.coLocationInsights.length} Co-Locations`} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                        
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                          Data auto-loaded to map
                        </Typography>
                      </Box>
                    )}

                    {!circle.results && !circle.loading && circle.dwellInsights === undefined && !circle.participatedInCommonAnalysis && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}>
                        {drawCircles.filter(c => !c.isResultCircle).length > 1 
                          ? 'Use "Find Common" above to search all areas'
                          : 'Click "Analyze Area" to search for activity'
                        }
                      </Typography>
                    )}

                    {circle.participatedInCommonAnalysis && !circle.results?.length && !circle.loading && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}>
                        ↳ Part of common area analysis (see results above)
                      </Typography>
                    )}

                    {circle.results && circle.results.length === 0 && !circle.loading && !circle.participatedInCommonAnalysis && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}>
                        No activity found in this area
                        {circle.geoneutronData?.error && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            API: {circle.geoneutronData.error}
                          </Typography>
                        )}
                      </Typography>
                    )}
                      </>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Discovery Preview Dialog */}
      <Dialog 
        open={discoveryDialog.open} 
        onClose={() => !discoveryDialog.loading && setDiscoveryDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="primary" />
          Area Search Discovery
        </DialogTitle>
        <DialogContent>
          {discoveryDialog.loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Discovering devices in area...
              </Typography>
            </Box>
          ) : discoveryDialog.discovery ? (
            <Box>
              {/* Error State */}
              {discoveryDialog.discovery.deviceCount === -1 ? (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Discovery Failed
                    </Typography>
                    <Typography variant="body2">
                      {discoveryDialog.strategy?.reason || 'Unable to query this area. Please try re-drawing the area or adjusting the date range.'}
                    </Typography>
                  </Alert>
                  <Typography variant="caption" color="text.secondary">
                    This can happen if the area coordinates are invalid or the external API is temporarily unavailable.
                    Try closing this dialog, editing the area, and running analysis again.
                  </Typography>
                </>
              ) : (
                <>
                  {/* Device Count Summary */}
                  <Box sx={{ 
                    border: 2,
                    borderColor: 'primary.main',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: 3,
                    mb: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      {discoveryDialog.discovery.deviceCount.toLocaleString()}
                    </Typography>
                    <Typography variant="subtitle1" color="text.primary">
                      Devices Found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {discoveryDialog.discovery.signalCount.toLocaleString()} total signals over {discoveryDialog.discovery.totalDays} days
                    </Typography>
                  </Box>

                  {/* Strategy Explanation */}
                  <Alert 
                    severity={
                      discoveryDialog.discovery.deviceCount <= 100 ? 'success' :
                      discoveryDialog.discovery.deviceCount <= 500 ? 'info' :
                      discoveryDialog.discovery.deviceCount <= 2000 ? 'warning' : 'error'
                    }
                    variant="outlined"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {discoveryDialog.strategy?.name === 'single_request' && 'Fast Query (Single Request)'}
                      {discoveryDialog.strategy?.name === 'temporal_partitioning' && 'Optimized Query (3-day chunks)'}
                      {discoveryDialog.strategy?.name === 'temporal_aggressive' && 'Heavy Query (2-day chunks)'}
                      {discoveryDialog.strategy?.name === 'temporal_very_aggressive' && 'Very Heavy Query (Daily chunks)'}
                      {discoveryDialog.strategy?.name === 'temporal_extreme' && '🔥 Extreme Query (12-hour chunks)'}
                    </Typography>
                    <Typography variant="body2">
                      {discoveryDialog.strategy?.reason}
                    </Typography>
                  </Alert>

                  {/* Technical Details */}
                  <Box sx={{ 
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    borderRadius: 1, 
                    p: 2, 
                    mb: 2 
                  }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                      Query Strategy Details:
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Time Chunks:</Typography>
                        <Typography variant="body2" color="text.primary">{discoveryDialog.strategy?.chunkDays} days each</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Parallel Requests:</Typography>
                        <Typography variant="body2" color="text.primary">{discoveryDialog.strategy?.maxConcurrent} concurrent</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Est. Time:</Typography>
                        <Typography variant="body2" color="text.primary">{discoveryDialog.strategy?.estimatedTime}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Area:</Typography>
                        <Typography variant="body2" color="text.primary">{discoveryDialog.circle?.name || discoveryDialog.circle?.id}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Proceed to fetch full location data for all {discoveryDialog.discovery.deviceCount} devices?
                  </Typography>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDiscoveryDialog(prev => ({ ...prev, open: false }))}
            disabled={discoveryDialog.loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              const { circle, flowType } = discoveryDialog;
              if (!circle) {
                console.error('[Discovery] No circle in dialog state!');
                return;
              }
              
              // Close dialog first
              setDiscoveryDialog(prev => ({ ...prev, open: false }));
              
              // Then trigger the appropriate search
              if (flowType === 'full_analysis') {
                performFullAnalysisDirect(circle);
              } else {
                performAreaSearchDirect(circle);
              }
            }}
            variant="contained"
            disabled={discoveryDialog.loading || !discoveryDialog.discovery || discoveryDialog.discovery.deviceCount === -1}
            startIcon={<SearchIcon />}
          >
            {discoveryDialog.flowType === 'full_analysis' ? 'Proceed with Analysis' : 'Proceed with Search'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Geofence Naming Dialog */}
      <Dialog 
        open={geofenceNameDialog.open} 
        onClose={() => setGeofenceNameDialog({ open: false, circle: null, name: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save as Geofence</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Give this area a name for future reference. Saved geofences can be reused for analysis.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Geofence Name"
            value={geofenceNameDialog.name}
            onChange={(e) => setGeofenceNameDialog(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Downtown Office, School Area, etc."
            helperText={`${geofenceNameDialog.circle?.shapeType === 'circle' ? 'Circle' : 'Polygon'}: ${geofenceNameDialog.circle?.shapeType === 'circle' ? `${geofenceNameDialog.circle?.radius}m radius` : `${geofenceNameDialog.circle?.coordinates?.[0]?.length || 0} vertices`}`}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && geofenceNameDialog.name.trim()) {
                handleSaveGeofenceWithName();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeofenceNameDialog({ open: false, circle: null, name: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveGeofenceWithName} 
            variant="contained"
            disabled={!geofenceNameDialog.name.trim() || savingGeofence !== null}
            startIcon={savingGeofence ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingGeofence ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Geofence Manager Dialog */}
      <Dialog 
        open={showGeofenceManager} 
        onClose={() => {
          setShowGeofenceManager(false);
          setSelectedGeofenceIds([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Select Geofences to Load ({selectedGeofenceIds.length} selected)</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => {
                    if (selectedGeofenceIds.length === 0) return;
                    const toExport = savedGeofences.filter(g => selectedGeofenceIds.includes(g.id));
                    const polygons = toExport.map(c => c.geometry);
                    if (polygons.length === 0) return;
                    const geojson = polygonsToFeatureCollection(polygons);
                    downloadGeoJSON(geojson);
                  }}
                >
              </Button>
              <IconButton size="small" onClick={() => {
                setShowGeofenceManager(false);
                setSelectedGeofenceIds([]);
              }}>
                <ClearIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {savedGeofences.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No saved geofences yet. Draw an area and click "Save Geofence" to create one.
              </Typography>
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  Select one or more geofences. With multiple selections, you can find devices that appear in <strong>all selected areas</strong>.
                </Typography>
              </Alert>
              <List>
                {Array.isArray(savedGeofences) && savedGeofences.map(geofence => {
                  const isSelected = selectedGeofenceIds.includes(geofence.id);
                  const handleToggle = () => {
                    setSelectedGeofenceIds(prev => 
                      prev.includes(geofence.id) 
                        ? prev.filter(id => id !== geofence.id)
                        : [...prev, geofence.id]
                    );
                  };
                  return (
                    <ListItem 
                      key={geofence.id}
                      sx={{ 
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isSelected ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.light' : 'action.hover'
                        }
                      }}
                      disablePadding
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={handleToggle}
                        sx={{ ml: 1, mr: 1 }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight="bold">
                            {geofence.name}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              {geofence.description}
                            </Typography>
                            <Typography component="span" variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Created: {new Date(geofence.created_at).toLocaleDateString()}
                              {geofence.created_by && ` • By: ${geofence.created_by}`}
                            </Typography>
                          </>
                        }
                        onClick={handleToggle}
                        sx={{ cursor: 'pointer' }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedNotesEntityId(geofence.id);
                            setSelectedNotesEntityType('geofence');
                            setSelectedNotesEntityTitle(`Notes for ${geofence.name}`);
                            setNotesDialogOpen(true);
                          }}
                          sx={{ mr: 1, color: 'text.secondary' }}
                          title="View Notes"
                        >
                          <NoteIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="Delete geofence">
                          <IconButton 
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete geofence "${geofence.name}"? This cannot be undone.`)) {
                                deleteGeofence(geofence.id);
                              }
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
          {selectedGeofenceIds.length > 1 && (
            <Alert severity="success" sx={{ width: '100%' }}>
              <Typography variant="caption">
                <strong>Multi-Area Analysis:</strong> Searching {selectedGeofenceIds.length} areas will find devices that appear in <strong>ALL</strong> selected areas (intersection analysis).
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button onClick={() => {
              setShowGeofenceManager(false);
              setSelectedGeofenceIds([]);
            }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              disabled={selectedGeofenceIds.length === 0}
              onClick={() => {
                const selected = savedGeofences.filter(g => selectedGeofenceIds.includes(g.id));
                selected.forEach(geofence => loadGeofence(geofence));
                setShowGeofenceManager(false);
                setSelectedGeofenceIds([]);
              }}
              fullWidth
            >
              Load {selectedGeofenceIds.length > 0 ? `${selectedGeofenceIds.length}` : ''} Selected Geofence{selectedGeofenceIds.length !== 1 ? 's' : ''}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Device Manager Dialog - Select and Load Saved Devices */}
      <Dialog
        open={showDeviceManager}
        onClose={() => {
          setShowDeviceManager(false);
          setSelectedDeviceIds([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Saved Devices Watchlist</span>
            <IconButton size="small" onClick={() => {
              setShowDeviceManager(false);
              setSelectedDeviceIds([]);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexShrink: 0 }}>
            Select saved devices to load their Pattern of Life data
          </Typography>
          
          <List sx={{ flex: 1, overflow: 'auto', minHeight: 0, maxHeight: '60vh' }}>
            {savedDevices.map(device => {
              const isSelected = selectedDeviceIds.includes(device.id);
              const handleToggle = () => {
                setSelectedDeviceIds(prev => 
                  prev.includes(device.id)
                    ? prev.filter(id => id !== device.id)
                    : [...prev, device.id]
                );
              };
              return (
                <ListItem
                  key={device.id}
                  sx={{
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: isSelected ? 'primary.dark' : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'action.hover'
                    }
                  }}
                  disablePadding
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={handleToggle}
                    sx={{ ml: 1, mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {device.name || `Device ${device.registration_id.slice(0, 8)}`}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', display: 'block', fontSize: '0.75rem' }}>
                          ID: {device.registration_id}
                        </Typography>
                        {device.notes && (
                          <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                            {device.notes}
                          </Typography>
                        )}
                        {device.tags && device.tags.length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {device.tags.map((tag: string, idx: number) => (
                              <Chip key={idx} label={tag} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                            ))}
                          </Box>
                        )}
                      </>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    onClick={handleToggle}
                    sx={{ cursor: 'pointer' }}
                  />
                <ListItemSecondaryAction>
                  <Tooltip title="Rename device">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameWatchlistDialog({
                          open: true,
                          deviceId: device.id,
                          name: device.name || `Device ${device.registration_id.slice(0, 8)}`,
                        });
                      }}
                      sx={{ mr: 0.5, color: 'text.secondary' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedNotesEntityId(device.id);
                      setSelectedNotesEntityType('watchlist');
                      setSelectedNotesEntityTitle(`Notes for ${device.name || device.registration_id}`);
                      setNotesDialogOpen(true);
                    }}
                    sx={{ mr: 1, color: 'text.secondary' }}
                    title="View/Add Notes"
                  >
                    <NoteIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title="Remove from watchlist">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDevice(device.id, device.name || device.registration_id);
                      }}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button onClick={() => {
              setShowDeviceManager(false);
              setSelectedDeviceIds([]);
            }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              disabled={selectedDeviceIds.length === 0 || isLoadingPoL}
              startIcon={isLoadingPoL ? <CircularProgress size={20} color="inherit" /> : null}
              onClick={async () => {
                setIsLoadingPoL(true);
                const selectedDeviceObjs = savedDevices.filter(d => selectedDeviceIds.includes(d.id));
                const regIds = selectedDeviceObjs.map(d => d.registration_id);
                
                
                try {
                  // Use current date range
                  const startDate = dateRange.start || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                  const endDate = dateRange.end || new Date();
                  
                  
                  // Load Pattern of Life for selected devices
                  const patternData = await pathfinderApi.getGeoNeutronPatternOfLife({
                    registrationIds: regIds,
                    startDate: startDate.toISOString().replace(/\.\d{3}Z$/, 'Z'),
                    endDate: endDate.toISOString().replace(/\.\d{3}Z$/, 'Z')
                  });
                  
                  
                  if (patternData && patternData.devices) {
                    // Convert to PathfinderDataPoints
                    const allDataPoints: PathfinderDataPoint[] = [];
                    Object.entries(patternData.devices).forEach(([_, locations]) => {
                      const deviceLocations = locations as any[];
                      deviceLocations.forEach(loc => {
                        allDataPoints.push({
                          id: `saved-pol-${loc.registration_id}-${loc.timestamp}`,
                          studentId: `GN-${loc.registration_id}`,
                          deviceId: loc.registration_id,
                          deviceType: 'phone',
                          coordinates: [loc.latitude, loc.longitude], // Internal format: [lat, lng]
                          timestamp: new Date(loc.timestamp),
                          accuracy: loc.accuracy,
                          metadata: {
                            altitude: loc.altitude,
                            speed: loc.speed,
                            bearing: loc.bearing,
                            ip_address: loc.ip_address,
                            forensic_flag: loc.forensic_flag,
                            country: loc.country,
                            carrier: loc.carrier,
                            timezone: loc.timezone
                          }
                        } as PathfinderDataPoint);
                      });
                    });
                    
                    
                    if (allDataPoints.length > 0 && onLoadPoLData) {
                      onLoadPoLData(allDataPoints, undefined, regIds.join(','));
                      alert(`Loaded ${allDataPoints.length} location points for ${regIds.length} saved devices!`);
                    } else {
                      alert('No Pattern of Life data found for the selected devices in the current date range.');
                    }
                  } else {
                    alert('No Pattern of Life data found for the selected devices in the current date range.');
                  }
                  
                  setShowDeviceManager(false);
                  setSelectedDeviceIds([]);
                } catch (error: any) {
                  console.error('Pattern of Life load error:', error);
                  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                    alert(`Request timed out.\n\nThe Pattern of Life search took too long.\n\nTry:\n- Reducing the date range\n- Selecting fewer devices at once`);
                  } else {
                    alert(`Failed to load Pattern of Life: ${error?.message || 'Check console for details.'}`);
                  }
                } finally {
                  setIsLoadingPoL(false);
                }
              }}
              fullWidth
            >
              {isLoadingPoL ? 'Loading...' : `Load PoL for ${selectedDeviceIds.length > 0 ? `${selectedDeviceIds.length}` : ''} Selected Device${selectedDeviceIds.length !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Rename saved watchlist device */}
      <Dialog
        open={renameWatchlistDialog.open}
        onClose={() =>
          !renameWatchlistSaving &&
          setRenameWatchlistDialog({ open: false, deviceId: null, name: '' })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename saved device</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Display name"
            fullWidth
            value={renameWatchlistDialog.name}
            onChange={(e) =>
              setRenameWatchlistDialog((prev) => ({ ...prev, name: e.target.value }))
            }
            disabled={renameWatchlistSaving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void (async () => {
                  const id = renameWatchlistDialog.deviceId;
                  if (!id) return;
                  const trimmed = renameWatchlistDialog.name.trim();
                  if (!trimmed) {
                    alert('Name cannot be empty');
                    return;
                  }
                  setRenameWatchlistSaving(true);
                  try {
                    await pathfinderApi.updateWatchlistDevice(id, { name: trimmed });
                    const devices = await pathfinderApi.getWatchlist();
                    setSavedDevices(devices || []);
                    window.dispatchEvent(new CustomEvent('refresh-watchlist'));
                    setRenameWatchlistDialog({ open: false, deviceId: null, name: '' });
                  } catch (err: any) {
                    alert(
                      err?.response?.data?.detail ||
                        err?.message ||
                        'Failed to rename device'
                    );
                  } finally {
                    setRenameWatchlistSaving(false);
                  }
                })();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setRenameWatchlistDialog({ open: false, deviceId: null, name: '' })
            }
            disabled={renameWatchlistSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={renameWatchlistSaving}
            onClick={async () => {
              const id = renameWatchlistDialog.deviceId;
              if (!id) return;
              const trimmed = renameWatchlistDialog.name.trim();
              if (!trimmed) {
                alert('Name cannot be empty');
                return;
              }
              setRenameWatchlistSaving(true);
              try {
                await pathfinderApi.updateWatchlistDevice(id, { name: trimmed });
                const devices = await pathfinderApi.getWatchlist();
                setSavedDevices(devices || []);
                window.dispatchEvent(new CustomEvent('refresh-watchlist'));
                setRenameWatchlistDialog({ open: false, deviceId: null, name: '' });
              } catch (err: any) {
                alert(
                  err?.response?.data?.detail ||
                    err?.message ||
                    'Failed to rename device'
                );
              } finally {
                setRenameWatchlistSaving(false);
              }
            }}
          >
            {renameWatchlistSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <NotesDialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        title={selectedNotesEntityTitle}
        watchlistId={selectedNotesEntityType === 'watchlist' ? selectedNotesEntityId : undefined}
        geofenceId={selectedNotesEntityType === 'geofence' ? selectedNotesEntityId : undefined}
      />
    </Box>

  );
};

export default AreaSearch;
